// ---------------------------------------------------------------------------
// Load state for the commerce dashboard hooks.
//
// Reported defect: every one of `use-revenue-data`, `use-products-data`,
// `use-orders-data` and `use-audience-data` caught its fetch rejection and
// silently substituted generated data — invented revenue ($1,240), invented
// people ("Sarah K."), invented fans (1,247) — with no error state and no
// on-screen marking. The chart series was `Math.random()`-based, so the numbers
// changed on every reload while still being presented as the viewer's own data.
//
// The rule applied here is the one already used by `studio-billing-source.ts`:
// a failed read is reported as a failure, never degraded into a confident
// answer. There is no fallback data path any more — `data` is only ever what a
// fetch returned.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react'

export type StudioReviewUnavailableReasonCode =
  | 'producer-not-configured'
  | 'metric-not-supported'
  | 'authority-not-admitted'

export type StudioReviewRead<T> =
  | {
      state: 'measured'
      data: T
      observedAt: string
      evidenceRefs: string[]
    }
  | {
      state: 'empty'
      data: T
      observedAt: string
      evidenceRefs: string[]
    }
  | {
      state: 'unavailable'
      reasonCode: StudioReviewUnavailableReasonCode
      message: string
    }

export type DashboardDataStatus = 'loading' | 'ready' | 'unavailable' | 'error'

export interface DashboardSlice<T> {
  status: DashboardDataStatus
  /** The authoritative successful read branch; never inferred from data shape. */
  reviewState?: 'measured' | 'empty'
  /** Only measured/authoritative-empty reads carry data. */
  data: T | null
  observedAt?: string
  evidenceRefs: string[]
  unavailableReasonCode?: StudioReviewUnavailableReasonCode
  unavailableReason?: string
}

const REVIEW_READ_STATES = new Set(['measured', 'empty', 'unavailable'])
const UNAVAILABLE_REASON_CODES = new Set([
  'producer-not-configured',
  'metric-not-supported',
  'authority-not-admitted',
])

function loadingSlice<T>(): DashboardSlice<T> {
  return { status: 'loading', data: null, evidenceRefs: [] }
}

function errorSlice<T>(): DashboardSlice<T> {
  return { status: 'error', data: null, evidenceRefs: [] }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const RFC3339_TIMESTAMP =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-](\d{2}):(\d{2}))$/

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
}

function hasValidObservation(value: Record<string, unknown>): boolean {
  if (typeof value.observedAt !== 'string') return false
  const match = RFC3339_TIMESTAMP.exec(value.observedAt)
  if (!match) return false

  const [
    ,
    yearText,
    monthText,
    dayText,
    hourText,
    minuteText,
    secondText,
    offsetHourText,
    offsetMinuteText,
  ] = match
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const hour = Number(hourText)
  const minute = Number(minuteText)
  const second = Number(secondText)

  if (year < 1 || month < 1 || month > 12) return false
  const daysInMonth = [
    31,
    isLeapYear(year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ]
  if (day < 1 || day > daysInMonth[month - 1]) return false
  if (hour > 23 || minute > 59 || second > 59) return false

  if (offsetHourText !== undefined) {
    const offsetHour = Number(offsetHourText)
    const offsetMinute = Number(offsetMinuteText)
    if (offsetHour > 23 || offsetMinute > 59) return false
  }

  return true
}

function hasStableEvidenceRefs(value: Record<string, unknown>): boolean {
  if (!Array.isArray(value.evidenceRefs) || value.evidenceRefs.length === 0) return false
  const refs = value.evidenceRefs
  if (!refs.every((ref) => typeof ref === 'string' && ref.trim().length > 0)) return false
  return new Set(refs).size === refs.length
}

function isStudioReviewRead<T>(value: unknown): value is StudioReviewRead<T> {
  if (!isRecord(value) || !REVIEW_READ_STATES.has(String(value.state))) return false

  if (value.state === 'unavailable') {
    return (
      UNAVAILABLE_REASON_CODES.has(String(value.reasonCode)) &&
      typeof value.message === 'string' &&
      value.message.trim().length > 0 &&
      !('data' in value) &&
      !('observedAt' in value) &&
      !('evidenceRefs' in value)
    )
  }

  return (
    (value.state === 'measured' || value.state === 'empty') &&
    'data' in value &&
    hasValidObservation(value) &&
    hasStableEvidenceRefs(value) &&
    !('reasonCode' in value) &&
    !('message' in value)
  )
}

function toDashboardSlice<T>(value: unknown): DashboardSlice<T> {
  if (!isStudioReviewRead<T>(value)) {
    throw new Error('Malformed Studio review read')
  }

  if (value.state === 'unavailable') {
    return {
      status: 'unavailable',
      data: null,
      evidenceRefs: [],
      unavailableReasonCode: value.reasonCode,
      unavailableReason: value.message,
    }
  }

  return {
    status: 'ready',
    reviewState: value.state,
    data: value.data,
    observedAt: value.observedAt,
    evidenceRefs: [...value.evidenceRefs],
  }
}

/**
 * Run one dashboard read and expose loading / ready / unavailable / error.
 *
 * On rejection or a malformed fulfilled value, the cause goes to
 * `console.error` (developers) and the slice goes to `error` (users). The
 * message itself is never rendered, so a backend string cannot leak into UI.
 */
export function useDashboardSlice<T>(
  label: string,
  load: () => Promise<StudioReviewRead<T>>,
  deps: unknown[],
  reloadToken: number,
): DashboardSlice<T> {
  const [slice, setSlice] = useState<DashboardSlice<T>>(() => loadingSlice())
  const requestRef = useRef(0)
  // `load` is re-created every render by callers; `deps` is the real identity.
  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    const requestId = ++requestRef.current
    const loader = loadRef.current
    let cancelled = false
    const isCurrent = () => !cancelled && requestRef.current === requestId

    setSlice(loadingSlice())

    Promise.resolve()
      // StrictMode cleans up the first mount effect before this microtask.
      // Do not start that stale read: otherwise the dev/browser build issues
      // two requests and a second success can conceal the first failure before
      // the user ever sees the explicit retry state.
      .then(() => isCurrent() ? loader() : undefined)
      .then((value) => {
        if (isCurrent()) setSlice(toDashboardSlice<T>(value))
      })
      .catch((cause) => {
        if (!isCurrent()) return
        // Swallowing this is what produced the reported defect. Keep the cause
        // visible to developers, and tell the user the read failed.
        console.error(`[studio-dashboard] failed to load ${label}`, cause)
        setSlice(errorSlice())
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadToken])

  return slice
}

/** A counter the pages bump from a "Try again" control to re-run every read. */
export function useReloadToken(): [number, () => void] {
  const [token, setToken] = useState(0)
  const reload = useCallback(() => setToken((current) => current + 1), [])
  return [token, reload]
}

export function anyLoading(slices: DashboardSlice<unknown>[]): boolean {
  return slices.some((slice) => slice.status === 'loading')
}
